import React, { type CSSProperties } from "react";
import { EMPRESA_VENCIMIENTO_EXPIRED_STYLE } from "../../shared/date/date-formats";

/** Fechas `YYYY-MM-DD` de la API como calendario local (evita desfase UTC → día anterior). */
export function parseDateForDisplay(value: Date | string | number): Date {
    if (value instanceof Date) return value;
    if (typeof value === "number") return new Date(value);
    const s = String(value).trim();
    const head = s.slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(head)) {
        const [y, m, d] = head.split("-").map(Number);
        return new Date(y, m - 1, d);
    }
    return new Date(s);
}

/** `true` si la fecha calendario es anterior a hoy (misma regla que indicador Vencido). */
export function isCalendarDateBeforeToday(value: Date | string | number | null | undefined): boolean {
    if (value == null || value === "") return false;
    const date = parseDateForDisplay(value);
    if (isNaN(date.getTime())) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    return day < today;
}

export function formatMonthShortText(date: Date, locale = "es-MX"): string {
    return new Intl.DateTimeFormat(locale, { month: "short" })
        .format(date)
        .replace(".", "");
}

export const formatDateCustom = (
    value: Date | string | number,
    locale = "es-MX"
): string => {
    const date = parseDateForDisplay(value);
    if (isNaN(date.getTime())) return "-";

    const day = String(date.getDate()).padStart(2, "0");
    const month = formatMonthShortText(date, locale);
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
};

export const formatWithPattern = (
    value: Date | string | number,
    pattern: string,
    locale = "es-MX",
) => {
    const date = parseDateForDisplay(value);
    if (isNaN(date.getTime())) return "-";

    const tokens: [string, string][] = [
        ["yyyy", String(date.getFullYear())],
        ["yy", String(date.getFullYear()).slice(-2)],
        ["MMM", formatMonthShortText(date, locale)],
        ["MM", String(date.getMonth() + 1).padStart(2, "0")],
        ["dd", String(date.getDate()).padStart(2, "0")],
        ["HH", String(date.getHours()).padStart(2, "0")],
        ["mm", String(date.getMinutes()).padStart(2, "0")],
    ];

    let result = pattern;
    for (const [token, replacement] of tokens) {
        result = result.split(token).join(replacement);
    }
    return result;
};

interface DateFormatProps {
    value?: Date | string | number | null;
    pattern?: string;
    locale?: string;
    highlightIfBeforeToday?: boolean;
    highlight?: boolean;
    expiredStyle?: CSSProperties;
}

const DateFormat = ({
    value,
    pattern,
    locale = "es-MX",
    highlightIfBeforeToday = false,
    highlight = false,
    expiredStyle = EMPRESA_VENCIMIENTO_EXPIRED_STYLE,
}: DateFormatProps) => {
    if (value == null || value === "") return <span>-</span>;

    const date = parseDateForDisplay(value);

    if (isNaN(date.getTime())) return <span>-</span>;

    const formatted = pattern
        ? formatWithPattern(date, pattern, locale)
        : formatDateCustom(date, locale);

    const expired = highlight || (highlightIfBeforeToday && isCalendarDateBeforeToday(date));

    return <span style={expired ? expiredStyle : undefined}>{formatted}</span>;
};

export default DateFormat;
